// composables/useAuthSessionRefresh.js
//
// Registers a visibilitychange + window.focus listener pair that silently
// refetches the user profile whenever the tab/window regains foreground.
//
// Registered ONCE in AppLayout.vue so every authenticated route benefits
// without per-page wiring. The composable is intentionally side-effect-only
// (no return value) because callers don't need to read its state.
//
// Design decisions (from plan Section 1.5):
//   - visibilitychange is the highest-yield trigger for "I just got an
//     activation confirm — let me check the app".
//   - focus backs it up for iOS Safari edge cases where visibility fires
//     unreliably across PWA / browser-tabs.
//   - Silent refresh (plan Section 1.5): skip the loading flash; the profile update
//     flows into the store reactively so UI updates seamlessly.
//   - Guard: skip if unauthenticated (unauthenticated refresh is a no-op
//     in useAuth, but saves the function call overhead).
//   - Guard: skip if document is not visible (focus fires occasionally while
//     a native share sheet is open; we don't want to trigger there).

import { onMounted, onUnmounted } from 'vue';
import { useAuth } from '@/composables/useAuth';

export function useAuthSessionRefresh() {
  const { refreshSession, isAuthenticated } = useAuth();

  function refetch() {
    if (!isAuthenticated.value) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    refreshSession({ silent: true }).catch(() => {
      // Errors are handled inside refreshSession (401 clears session via
      // interceptor; others log internally). We catch here to prevent
      // unhandled promise rejections from surfacing in the console.
    });
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', refetch);
    window.addEventListener('focus', refetch);
  });

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', refetch);
    window.removeEventListener('focus', refetch);
  });
}
